<script setup lang="ts">
const { t, tm, rt } = useI18n();
const site = useSiteData();
const { localized } = useLocaleValue();
const effectiveDate = "2025-12-01";
const tmList = (key: string) => tm(key) as unknown[];
const tmText = (item: unknown) => rt(item as Parameters<typeof rt>[0]);

definePageMeta({
  page_name: "privacy",
  robots: "noindex, follow",
});
</script>

<template>
  <section class="page-header">
    <div class="container">
      <h1>{{ t("privacy.header.title") }}</h1>
    </div>
  </section>

  <section class="about-content">
    <div class="container">
      <div class="about-section">
        <p>
          <strong>{{ t("privacy.effective_date_label") }}</strong>
          {{ effectiveDate }}
        </p>
        <p>{{ t("privacy.intro") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.info_collect.title") }}</h2>

        <h3>{{ t("privacy.sections.info_collect.provided.title") }}</h3>
        <p>{{ t("privacy.sections.info_collect.provided.p1") }}</p>
        <p>{{ t("privacy.sections.info_collect.provided.p2") }}</p>

        <h3>{{ t("privacy.sections.info_collect.auto.title") }}</h3>
        <p>{{ t("privacy.sections.info_collect.auto.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.info_collect.auto.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>{{ t("privacy.sections.info_collect.auto.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.usage.title") }}</h2>
        <p>{{ t("privacy.sections.usage.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.usage.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>{{ t("privacy.sections.usage.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.cookies.title") }}</h2>
        <p>{{ t("privacy.sections.cookies.p1") }}</p>
        <p v-html="t('privacy.sections.cookies.p2')" />
        <p v-html="t('privacy.sections.cookies.p3')" />
        <p>{{ t("privacy.sections.cookies.p4") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.legal_basis.title") }}</h2>
        <p>{{ t("privacy.sections.legal_basis.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.legal_basis.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.sharing.title") }}</h2>
        <p>{{ t("privacy.sections.sharing.p1") }}</p>
        <p>{{ t("privacy.sections.sharing.p2") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.sharing.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>{{ t("privacy.sections.sharing.p3") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.retention.title") }}</h2>
        <p>{{ t("privacy.sections.retention.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.retention.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.security.title") }}</h2>
        <p>{{ t("privacy.sections.security.p1") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.transfers.title") }}</h2>
        <p>{{ t("privacy.sections.transfers.p1") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.rights.title") }}</h2>
        <p>{{ t("privacy.sections.rights.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('privacy.sections.rights.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>{{ t("privacy.sections.rights.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.children.title") }}</h2>
        <p>{{ t("privacy.sections.children.p1") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.changes.title") }}</h2>
        <p>{{ t("privacy.sections.changes.p1") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("privacy.sections.contact.title") }}</h2>
        <p>{{ t("privacy.sections.contact.p1") }}</p>
        <p>
          <strong>{{ localized(site.company_full_legal_name) }}</strong><br />
          {{
            localized(site.legal_address.building_number)
          }}
          {{
            localized(site.legal_address.street_name)
          }}{{ t("common.comma") }}
          {{
            localized(site.legal_address.additional_number)
          }}
          {{
            localized(site.legal_address.district)
          }}{{ t("common.comma") }}
          {{
            localized(site.legal_address.postal_code)
          }}
          {{
            localized(site.legal_address.city)
          }}{{ t("common.comma") }}
          {{ localized(site.legal_address.country) }}<br />
          {{ t("privacy.sections.contact.email_label") }}
          <a :href="`mailto:${localized(site.official_email)}`">
            {{ localized(site.official_email) }}
          </a>
        </p>
        <p>
          {{ t("privacy.sections.contact.footer_prefix") }}
          <NuxtLink to="/contact">
            {{ t("privacy.sections.contact.contact_link_label") }}
          </NuxtLink>.
        </p>
      </div>
    </div>
  </section>
</template>

